import React, { useEffect, useRef, useState } from 'react';

// A figure on the sheet that copies itself when clicked.
//
// Only put on the bold ones - the subtotals, the run figures, total
// compensation, cash needed. Those are the numbers that get carried somewhere
// else: into Empower, into QuickBooks, into an email. The per-employee cells
// around them are read on the page and would only add a hundred click targets
// to a sheet somebody is trying to scan.
//
// What lands on the clipboard is the plain number - "12345.67", no currency
// symbol, no thousands separators - because everywhere it is going is a field
// or a cell that wants a number. The formatted version is for reading here.
export default function PayrollCopyableFigure({ value, label, children }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  // A component whose cell is re-rendered away mid-flash would otherwise set
  // state after unmounting.
  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const numeric = Number(value);
  const canCopy = Number.isFinite(numeric);

  const handleCopy = async () => {
    if (!canCopy) return;
    const text = numeric.toFixed(2);

    try {
      // navigator.clipboard needs a secure context. Falls back to the old
      // execCommand path rather than failing silently, since this runs on an
      // internal host that is not always https.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const scratch = document.createElement('textarea');
        scratch.value = text;
        scratch.setAttribute('readonly', '');
        scratch.style.position = 'fixed';
        scratch.style.opacity = '0';
        document.body.appendChild(scratch);
        scratch.select();
        document.execCommand('copy');
        document.body.removeChild(scratch);
      }

      setCopied(true);
      window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // Nothing useful to say and nothing broken on the page - the number is
      // still there to select by hand.
    }
  };

  if (!canCopy) return children;

  return (
    <button
      type="button"
      className={`payroll-copyfig${copied ? ' payroll-copyfig--copied' : ''}`}
      onClick={handleCopy}
      title={copied ? 'Copied' : 'Click to copy'}
      aria-label={`Copy ${label || 'figure'}: ${numeric.toFixed(2)}`}
    >
      {children}
      <i
        className={`payroll-copyfig__icon bi ${copied ? 'bi-check-lg' : 'bi-clipboard'}`}
        aria-hidden="true"
      />
    </button>
  );
}
